import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Snackbar,
  Collapse,
  AlertColor,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  SxProps,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Delete as DeleteIcon, 
  ArrowBack, 
  ArrowForward,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import ReCAPTCHA from "react-google-recaptcha";

// Import utilities from the consolidated utils file
import { 
  parseFormConfig, 
  validateField, 
  shouldShowField,
  formatFormDataForSubmission,
  FormEngineProps,
  FormStep,
  FormFieldRenderer
} from '../utils/FormEngineUtils';

// Optional PDF generation - configured via environment variables
const ENABLE_PDF_GENERATION = import.meta.env.VITE_ENABLE_PDF_GENERATION === 'true';

// Only import PDF functionality if enabled (to reduce bundle size)
let pdfLibrariesLoaded = false;
let generatePDF: any = null;

if (ENABLE_PDF_GENERATION) {
  // Load PDF libraries
  Promise.all([
    import('../utils/form/PDFGenerator'),
    import('@react-pdf/renderer')
  ]).then(([pdfGeneratorModule, reactPdfModule]) => {
    const { default: FormPDFDocument } = pdfGeneratorModule;
    const { pdf } = reactPdfModule;
    
    pdfLibrariesLoaded = true;
    
    // Create PDF generator function
    generatePDF = async (formData: Record<string, any>, steps: FormStep[], userData: any) => {
      try {
        const dateStr = new Date().toLocaleString();
        const pdfDoc = <FormPDFDocument 
          formData={formData} 
          steps={steps} 
          userData={userData}
          submissionDate={dateStr}
        />;
        const blob = await pdf(pdfDoc).toBlob();
        return URL.createObjectURL(blob);
      } catch (err) {
        console.error('Error generating PDF:', err);
        return null;
      }
    };
  }).catch(err => {
    console.error('Failed to load PDF libraries:', err);
  });
}

// Update the props interface to include requireRecaptcha
interface FormEngineProps {
  steps: FormStep[];
  onSubmit?: (formData: any, formattedData: any) => Promise<any>;
  userData?: any;
  formTitle?: string;
  formDescription?: string;
  // Shown after the description; may hold links, unlike the plain-text description.
  formNote?: React.ReactNode;
  formId?: string;
  isExpanded?: boolean;
  disableSubmit?: boolean;
  sx?: SxProps;
  requireRecaptcha?: boolean; // Add this prop
}

// Add this helper function at the top of your file (outside the component)
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data:mime/type;base64, prefix
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => {
      console.error('Error converting file to base64:', error);
      reject(error);
    };
  });
};

const FormEngine: React.FC<FormEngineProps> = ({ 
  steps: rawSteps, 
  onSubmit, 
  userData,
  formTitle: customFormTitle,
  formDescription: customFormDescription,
  formNote,
  formId = 'defaultForm',
  isExpanded = false,
  disableSubmit = false,
  sx = {},
  requireRecaptcha = false // Default to false for backward compatibility
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [parsedSteps, setParsedSteps] = useState<FormStep[]>([]);
  const [stepHistory, setStepHistory] = useState<number[]>([0]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('success');
  const [submitting, setSubmitting] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [configTitle, setConfigTitle] = useState(customFormTitle || '');
  const [configDescription, setConfigDescription] = useState(customFormDescription || '');

  // State for collapsible form
  const [isFormCollapsed, setIsFormCollapsed] = useState(!isExpanded);

  // Session storage key
  const storageKey = `formEngine_${formId}`;

  // State for success dialog
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [submissionResult, setSubmissionResult] = useState<{
    referenceNumber?: string;
    confirmationSent?: boolean;
  } | null>(null);

  // Add state for reCAPTCHA
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  // Add state for file attachments
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Update effect to handle isExpanded prop changes
  useEffect(() => {
    setIsFormCollapsed(!isExpanded);
  }, [isExpanded]);

  // Parse form configuration
  useEffect(() => {
    const { steps, title, description } = parseFormConfig(
      rawSteps,
      customFormTitle,
      customFormDescription
    );
    
    setParsedSteps(steps);
    setConfigTitle(title);
    setConfigDescription(description);
  }, [rawSteps, customFormTitle, customFormDescription]);

  // Load form data from session storage on initial render
  useEffect(() => {
    try {
      const savedData = sessionStorage.getItem(storageKey);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
        setSnackbarMessage('Form data loaded');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      }
    } catch (e) {
      console.error('Failed to load form data from session storage', e);
    }
  }, [storageKey]);

  // Save form data to session storage
  const saveToSession = () => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(formData));
      setSnackbarMessage('Form data saved');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (e) {
      console.error('Failed to save form data to session storage', e);
      setSnackbarMessage('Failed to save form data');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Clear session storage
  const clearSession = () => {
    try {
      sessionStorage.removeItem(storageKey);
      setFormData({});
      setCurrentStep(0);
      setStepHistory([0]);
      setSnackbarMessage('Form data cleared');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
    } catch (e) {
      console.error('Failed to clear form data from session storage', e);
    }
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Handle field change
  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
    
    // Clear error when field is changed
    if (errors[fieldId]) {
      setErrors(prev => ({
        ...prev,
        [fieldId]: ''
      }));
    }
  };

  // Download PDF function
  const downloadPDF = async () => {
    if (!ENABLE_PDF_GENERATION || !pdfLibrariesLoaded || !generatePDF) {
      console.log('PDF generation not available');
      return;
    }
    
    try {
      // Use existing PDF URL if available
      if (pdfUrl) {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `form_submission_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      
      // Generate new PDF if needed
      const newPdfUrl = await generatePDF(formData, parsedSteps, userData);
      if (!newPdfUrl) {
        console.error('Failed to generate PDF');
        return;
      }
      
      // Set PDF URL for future use
      setPdfUrl(newPdfUrl);
      
      // Create an anchor and trigger download
      const link = document.createElement('a');
      link.href = newPdfUrl;
      link.download = `form_submission_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL
      setTimeout(() => URL.revokeObjectURL(newPdfUrl), 100);
    } catch (err) {
      console.error('Error downloading PDF:', err);
    }
  };

  const findNextStep = (
    currentStepData: FormStep,
    formData: Record<string, any>,
    steps: FormStep[]
  ): number => {
    // If no flow rules exist, return -1 to use sequential flow
    if (!currentStepData.flowRules || currentStepData.flowRules.length === 0) {
      return -1;
    }

    for (const rule of currentStepData.flowRules) {
      // Special case: if both field and value are "*", it's a wildcard rule that always matches
      if (rule.field === "*" && rule.value === "*" && rule.operator === "equals") {
        const targetIndex = steps.findIndex(step => step.id === rule.targetStepId);
        if (targetIndex !== -1) {
          return targetIndex;
        }
        continue;
      }

      const fieldValue = formData[rule.field];
      const fieldExists = rule.field in formData;

      // Handle different operators
      let isMatch = false;
      
      if (rule.operator === "equals") {
        // For equals operator, handle special cases
        isMatch = (
          // Empty string in rule matches empty or undefined field value
          (rule.value === "" && (!fieldExists || fieldValue === "" || fieldValue === undefined)) ||
          // Wildcard "*" matches any non-empty value
          (rule.value === "*" && fieldExists && fieldValue !== "" && fieldValue !== undefined) ||
          // Exact match
          fieldValue === rule.value
        );
      } else {
        // Default matching behavior for other cases
        isMatch = (
          // Wildcard "*" matches any non-empty value
          (rule.value === "*" && fieldExists && fieldValue !== "") ||
          // Empty string "" in rule matches any value (including undefined)
          (rule.value === "" && fieldExists) ||
          // Regular expression matching if isRegex is true
          (rule.isRegex && typeof fieldValue === 'string' && new RegExp(rule.value).test(fieldValue)) ||
          // Exact value match
          fieldValue === rule.value
        );
      }

      if (isMatch) {
        const targetIndex = steps.findIndex(step => step.id === rule.targetStepId);
        if (targetIndex !== -1) {
          return targetIndex;
        }
      }
    }

    return -1; // No matching rule found
  };

  const handleNext = () => {
    if (parsedSteps.length === 0) return;
    
    const currentStepData = parsedSteps[currentStep];
    
    // Check if current step is a completion step
    if (currentStepData.completionType === 'submit' || currentStepData.completionType === 'restart') {
      return;
    }
    
    const currentFields = currentStepData.fields;
    const newErrors: Record<string, string> = {};

    currentFields.forEach(field => {
      if (shouldShowField(field, formData)) {
        const error = validateField(field, formData[field.id]);
        if (error) {
          newErrors[field.id] = error;
        }
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Find next step using flow rules
      const nextStepIndex = findNextStep(currentStepData, formData, parsedSteps);
      
      if (nextStepIndex !== -1) {
        setStepHistory(prev => [...prev, nextStepIndex]);
        setCurrentStep(nextStepIndex);
      } else if (currentStep < parsedSteps.length - 1) {
        // Fallback to next sequential step if no flow rules match
        setStepHistory(prev => [...prev, currentStep + 1]);
        setCurrentStep(currentStep + 1);
      } else {
        // Last step reached, handle submission
        handleSubmit();
      }
    }
  };

  const handlePrevious = () => {
    if (stepHistory.length > 1) {
      const newHistory = [...stepHistory];
      newHistory.pop(); // Remove current step
      const previousStep = newHistory[newHistory.length - 1];
      setStepHistory(newHistory);
      setCurrentStep(previousStep);
    }
  };

  // Handle reCAPTCHA change
  const handleCaptchaChange = (value: string | null) => {
    console.log('reCAPTCHA value:', value);
    setCaptchaValue(value);
    if (value) {
      setCaptchaError(null);
    }
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    
    // Check file size (limit to 5MB per file)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20MB total
    
    // Calculate current total size
    const currentTotalSize = attachments.reduce((total, file) => total + file.size, 0);
    
    // Convert FileList to array and filter out files that are too large
    const newFiles = Array.from(files).filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(`File "${file.name}" exceeds the 5MB limit`);
        return false;
      }
      return true;
    });
    
    // Check if adding these files would exceed total limit
    const newTotalSize = currentTotalSize + newFiles.reduce((total, file) => total + file.size, 0);
    if (newTotalSize > MAX_TOTAL_SIZE) {
      setUploadError('Total attachments exceed 20MB limit');
      return;
    }
    
    // Add files to state
    setAttachments(prev => [...prev, ...newFiles]);
    setUploadError(null);
  };
  
  // Remove attachment
  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  
  // Modify handleSubmit to include attachments
  const handleSubmit = async () => {
    if (disableSubmit || submitting) return;

    // Validate current step fields before submission
    const currentStepData = parsedSteps[currentStep];
    const currentFields = currentStepData.fields;
    const newErrors: Record<string, string> = {};

    currentFields.forEach(field => {
      if (shouldShowField(field, formData)) {
        const error = validateField(field, formData[field.id]);
        if (error) {
          newErrors[field.id] = error;
        }
      }
    });

    setErrors(newErrors);

    // If there are validation errors, don't proceed with submission
    if (Object.keys(newErrors).length > 0) {
      setSnackbarMessage('Please fix the errors before submitting');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Check if reCAPTCHA is required but not completed
    if (requireRecaptcha && !import.meta.env.DEV && !captchaValue) {
      setCaptchaError('Please complete the reCAPTCHA verification');
      setSnackbarMessage('Please complete the reCAPTCHA verification');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setSubmitting(true);
    
    try {
      // Create enhanced formatted data that includes field statements
      const formattedData = formatFormDataForSubmission(formData, parsedSteps);
      
      // Enhance the formatted data to include field statements
      const enhancedFormattedData = parsedSteps.map(step => {
        // Get all fields, including those with fieldStatements
        const stepFields = step.fields.map(field => {
          return {
            id: field.id,
            label: field.label,
            type: field.type,
            fieldStatement: field.fieldStatement || '',
            value: formData[field.id]
          };
        }).filter(field => field.value !== undefined && field.value !== null && field.value !== '');
        
        return {
          id: step.id,
          title: step.title,
          description: step.description,
          displayStatement: step.displayStatement,
          fields: stepFields
        };
      });
      
      if (onSubmit) {
        // Process files in smaller batches if there are many
        const MAX_BATCH_SIZE = 3; // Process max 3 files at once
        const fileAttachments = [];
        
        // Process files in batches to avoid memory issues
        for (let i = 0; i < attachments.length; i += MAX_BATCH_SIZE) {
          const batch = attachments.slice(i, i + MAX_BATCH_SIZE);
          const batchResults = await Promise.all(
            batch.map(async (file) => {
              try {
                return {
                  filename: file.name,
                  content: await fileToBase64(file),
                  contentType: file.type
                };
              } catch (error) {
                console.error(`Error processing file ${file.name}:`, error);
                setSnackbarMessage(`Error processing file ${file.name}. Please try again.`);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return null;
              }
            })
          );
          
          // Filter out any failed conversions
          fileAttachments.push(...batchResults.filter(result => result !== null));
        }
        
        // Add attachments and enhanced data to form data
        const formDataWithAttachments = {
          ...formData,
          _attachments: fileAttachments,
          _enhancedFormData: enhancedFormattedData
        };
        
        // Call the onSubmit callback with the updated form data
        const result = await onSubmit(formDataWithAttachments, formattedData);
        
        console.log('Submit result:', result);

        if (result?.success) {
          // Get reference number directly from the response
          const referenceNumber = result.referenceNumber;
          console.log('Reference number:', referenceNumber);
          
          // Set the submission result with reference number
          setSubmissionResult({
            referenceNumber: referenceNumber,
            confirmationSent: result.confirmationSent
          });
          
          // Mark form as submitted
          setIsFormSubmitted(true);
          
          // Reset captcha after successful submission
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
          setCaptchaValue(null);
          
          // Show success dialog
          setShowSuccessDialog(true);
          setSubmitting(false);
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbarMessage('Failed to submit form. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setSubmitting(false);
    }
  };

  // Handle restart
  const handleRestart = () => {
    setFormData({});
    setCurrentStep(0);
    setStepHistory([0]);
    setErrors({});
    setPdfUrl(null);
    setSnackbarMessage('Form restarted');
    setSnackbarSeverity('info');
    setSnackbarOpen(true);
  };

  // Simplified empty steps check
  if (!parsedSteps || parsedSteps.length === 0) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: { xs: '16px', sm: '24px' },
          background: '#ffffff',
        }}
      >
        <Typography variant="h6" color="error" textAlign="center">
          No form steps found or invalid configuration
        </Typography>
      </Paper>
    );
  }

  // Make sure currentStep is valid
  const safeCurrentStep = Math.min(currentStep, parsedSteps.length - 1);
  const currentStepData = parsedSteps[safeCurrentStep];

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    setSubmitting(false);
    window.location.href = '/'; // Redirect to home
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, margin: '0 auto', ...sx }}>
      {/* Collapsible header with form title and description */}
      <Paper 
        onClick={() => setIsFormCollapsed(!isFormCollapsed)}
        sx={{ 
          p: 2, 
          mb: isFormCollapsed ? 0 : 2, 
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              {configTitle || 'Support Form'}
            </Typography>
            {(configDescription || formNote) && (
              <Typography variant="body2" color="text.secondary">
                {configDescription}
                {configDescription && formNote && ' '}
                {formNote}
              </Typography>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {isFormCollapsed ? 'Show Form' : 'Hide Form'}
          </Typography>
        </Box>
      </Paper>

      {/* Form content */}
      <Collapse in={!isFormCollapsed}>
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          {currentStepData && (
            <Box>
              {/* Show step title and description if present */}
              {currentStepData.title && (
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2,
                    fontSize: { 
                      xs: '1.1rem',  // Smaller on mobile
                      sm: '1.25rem'  // Default h6 size on larger screens
                    }
                  }}
                >
                  {currentStepData.title}
                </Typography>
              )}
              {currentStepData.description && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 3,
                    p: 2,
                    bgcolor: 'info.light',
                    color: 'info.contrastText',
                    borderRadius: 1,
                    fontSize: {
                      xs: '0.875rem',  // Smaller on mobile
                      sm: '0.875rem'   // Default body2 size
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: currentStepData.description }}
                />
              )}
              
              {/* Display statement if present */}
              {currentStepData.displayStatement && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
                  {(() => {
                    const lines = currentStepData.displayStatement?.split('\n') || [];
                    return lines.map((line, i) => (
                      <Typography 
                        key={i} 
                        variant="body2" 
                        sx={{ 
                          mb: i < lines.length - 1 ? 0.5 : 0,
                          color: 'info.contrastText',
                          fontSize: {
                            xs: '0.875rem',  // Smaller on mobile
                            sm: '0.875rem'   // Default body2 size
                          }
                        }}
                      >
                        {line}
                      </Typography>
                    ));
                  })()}
                </Paper>
              )}
              
              {/* Display fields */}
              <Box sx={{ mt: 3 }}>
                {currentStepData.fields.map((field) => {
                  // Skip fields that should not be shown based on conditions
                  if (!shouldShowField(field, formData)) return null;
                  
                  return (
                    <Box 
                      key={field.id} 
                      sx={{ 
                        mb: 4,  // Increased margin bottom
                        '& .MuiFormControl-root': {
                          width: '100%',  // Ensure form controls take full width
                        },
                        '& .MuiInputBase-root': {
                          minHeight: '56px',  // Minimum height for input fields
                        },
                        '& .MuiOutlinedInput-root': {
                          padding: '8px 14px',  // Consistent padding
                        },
                        '& .MuiInputLabel-root': {
                          lineHeight: '1.4',  // Improved label line height
                        }
                      }}
                    >
                      <FormFieldRenderer
                        field={field}
                        value={formData[field.id] !== undefined ? formData[field.id] : field.value}
                        onChange={handleFieldChange}
                        error={errors[field.id]}
                      />
                    </Box>
                  );
                })}
              </Box>

              {/* Navigation and action buttons */}
              <Box sx={{ 
                mt: 4, 
                display: 'flex', 
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 }
              }}>
                {/* Save/Clear buttons */}
                <Box>
                  <Button
                    onClick={saveToSession}
                    startIcon={<SaveIcon />}
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={clearSession}
                    startIcon={<DeleteIcon />}
                    color="error"
                    size="small"
                  >
                    Clear
                  </Button>
                </Box>

                {/* Navigation buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {currentStep > 0 && (
                    <Button 
                      onClick={handlePrevious}
                      startIcon={<ArrowBack />}
                    >
                      Back
                    </Button>
                  )}
                  {(!currentStepData.completionType || currentStepData.completionType === 'none') && (
                    <Button 
                      variant="contained" 
                      onClick={handleNext}
                      endIcon={<ArrowForward />}
                      disabled={disableSubmit || submitting}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </Box>
              
              {/* Add reCAPTCHA before submit button if required */}
              {requireRecaptcha && currentStepData.completionType === 'submit' && !isFormSubmitted && (
                <Box sx={{ 
                  mt: 3, 
                  mb: 3, 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%'
                }}>
                  <Typography variant="body2" sx={{ mb: 2, color: '#445668' }}>
                    Please complete the verification below to submit your request:
                  </Typography>
                  
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'} // Fallback to test key
                    onChange={handleCaptchaChange}
                  />
                  
                  {captchaError && (
                    <Typography 
                      variant="caption" 
                      color="error" 
                      sx={{ mt: 1 }}
                    >
                      {captchaError}
                    </Typography>
                  )}
                  
                  {/* Development mode indicator */}
                  {import.meta.env.DEV && (
                    <Typography 
                      variant="caption" 
                      align="center" 
                      sx={{ 
                        display: 'block',
                        mt: 2,
                        color: 'orange'
                      }}
                    >
                      Development mode: reCAPTCHA validation is bypassed
                    </Typography>
                  )}
                  
                  <Typography 
                    variant="caption" 
                    align="center" 
                    sx={{ 
                      display: 'block',
                      mt: 2,
                      color: '#445668',
                      '& a': {
                        color: '#0066b2',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      },
                    }}
                  >
                    This site is protected by reCAPTCHA and the Google{' '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </a>{' '}
                    apply.
                  </Typography>
                </Box>
              )}
              
              {/* Add file upload section before the submit button */}
              {currentStepData.completionType === 'submit' && (
                <Box sx={{ mt: 3, mb: 3, p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Attachments
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Upload additional information or history (Max 5MB per file, 20MB total)
                  </Typography>
                  
                  {/* File input */}
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AttachFileIcon />}
                    sx={{ mt: 1 }}
                  >
                    Upload Files
                    <input
                      type="file"
                      multiple
                      hidden
                      onChange={handleFileUpload}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                    />
                  </Button>
                  
                  {/* Error message */}
                  {uploadError && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      {uploadError}
                    </Typography>
                  )}
                  
                  {/* List of uploaded files */}
                  {attachments.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Uploaded Files:
                      </Typography>
                      <List dense>
                        {attachments.map((file, index) => (
                          <ListItem 
                            key={index}
                            secondaryAction={
                              <IconButton edge="end" onClick={() => removeAttachment(index)}>
                                <DeleteIcon />
                              </IconButton>
                            }
                          >
                            <ListItemIcon>
                              <InsertDriveFileIcon />
                            </ListItemIcon>
                            <ListItemText 
                              primary={file.name} 
                              secondary={`${(file.size / 1024).toFixed(1)} KB`} 
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              )}
              
              {/* Completion buttons */}
              {currentStepData.completionType && (
                <Box sx={{ mt: 4, textAlign: 'center', display: 'flex', gap: 2, justifyContent: 'center' }}>
                  {currentStepData.completionType === 'submit' && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                      disabled={disableSubmit || submitting || (requireRecaptcha && !import.meta.env.DEV && !captchaValue)}
                    >
                      {submitting ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Submit'
                      )}
                    </Button>
                  )}
                  {currentStepData.completionType === 'restart' && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleRestart}
                      startIcon={<RefreshIcon />}
                    >
                      Start Over
                    </Button>
                  )}
                </Box>
              )}
              
              {/* PDF download section - only visible if PDF is available */}
              {ENABLE_PDF_GENERATION && (
                <Box sx={{ mt: 4 }}>
                  {pdfUrl ? (
                    <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1, color: 'success.contrastText' }}>
                      <Typography variant="body1" fontWeight="bold" gutterBottom>
                        PDF Ready
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        Your form submission has been processed. You can download a PDF copy for your records.
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<DownloadIcon />}
                        onClick={downloadPDF}
                        sx={{ mt: 1 }}
                      >
                        Download PDF
                      </Button>
                    </Box>
                  ) : (
                    submitting && (
                      <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, color: 'info.contrastText' }}>
                        <Typography variant="body2">
                          PDF generation will be available after submission...
                        </Typography>
                      </Box>
                    )
                  )}
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Collapse>

      {/* Success Dialog */}
      <Dialog
        open={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        aria-labelledby="success-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="success-dialog-title" sx={{ textAlign: 'center' }}>
          Form Submitted Successfully
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          {submissionResult?.referenceNumber ? (
            <Typography variant="h6" gutterBottom>
              Reference Number: {submissionResult.referenceNumber}
            </Typography>
          ) : (
            <Typography variant="body1" color="error" gutterBottom>
              Reference number not available
            </Typography>
          )}
          {submissionResult?.confirmationSent && (
            <Typography variant="body1" sx={{ mt: 2 }}>
              A confirmation email has been sent.
            </Typography>
          )}
          <Typography variant="body1" sx={{ mt: 3 }}>
            A member of our service team will contact you to discuss your concerns.
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Alternatively please contact us on (03) 9587 9900 with your reference number ready.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSuccessDialogClose}
            color="primary"
            size="large"
          >
            Return to Home
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification snackbar */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={3000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FormEngine;